import { supabase } from '../supabase';
import { v4 as uuidv4 } from 'uuid';

interface ActiveSession {
  id: string;
  user_id: string;
  device_info: string;
  ip_address?: string;
  user_agent: string;
  created_at: string;
  last_activity: string;
  is_active: boolean;
}

class SimpleSessionChecker {
  private currentSessionId: string | null = null;
  private isInitialized: boolean = false;
  private checkInterval: NodeJS.Timeout | null = null;
  private visibilityListener: (() => void) | null = null;
  private focusListener: (() => void) | null = null;

  // Inizializza il controllo sessioni SOLO dopo login completato
  public async initializeAfterLogin(userId: string): Promise<void> {
    // SEMPRE reinizializza - anche se già inizializzato
    // Questo gestisce il caso di login multipli consecutivi
    console.log('[SessionChecker] Initializing session checker for user:', userId, '- Force reinit:', this.isInitialized);
    
    try {
      // 1. Ferma eventuali controlli precedenti
      this.stopPeriodicCheck();
      
      // 2. Genera un ID sessione completamente nuovo
      this.currentSessionId = uuidv4();
      console.log('[SessionChecker] Generated new session ID:', this.currentSessionId);
      
      // 3. Disconnetti TUTTE le sessioni precedenti per questo utente
      await this.disconnectPreviousSessions(userId);
      
      // 4. Registra la nuova sessione
      await this.registerCurrentSession(userId);
      
      // 5. Avvia controllo periodico (ogni 30 secondi)
      this.startPeriodicCheck(userId);
      
      this.isInitialized = true;
      console.log('[SessionChecker] Session checker initialized successfully with ID:', this.currentSessionId);
      
    } catch (error) {
      console.error('[SessionChecker] Error during initialization:', error);
      // Non bloccare l'app se c'è un errore, continua normalmente
    }
  }

  private async disconnectPreviousSessions(userId: string): Promise<void> {
    try {
      // Disconnetti solo le sessioni WEB attive per questo utente
      // Le sessioni mobile sono permesse in parallelo
      console.log('[SessionChecker] Disconnecting active WEB sessions for user:', userId);
      
      // Disconnetti solo le sessioni WEB precedenti (non quelle mobile)
      const { data, error } = await supabase
        .from('user_sessions')
        .update({
          is_active: false,
          disconnected_at: new Date().toISOString(),
          disconnection_reason: 'new_login'
        })
        .eq('user_id', userId)
        .eq('is_active', true)
        .eq('client_type', 'web')
        .select();

      if (error) {
        console.error('[SessionChecker] Error disconnecting previous sessions:', error);
      } else {
        console.log('[SessionChecker] Disconnected', data?.length || 0, 'active sessions');
        if (data && data.length > 0) {
          console.log('[SessionChecker] Disconnected session IDs:', data.map(s => s.id));
        }
      }
    } catch (error) {
      console.error('[SessionChecker] Error in disconnectPreviousSessions:', error);
    }
  }

  private async registerCurrentSession(userId: string): Promise<void> {
    if (!this.currentSessionId) return;

    try {
      const deviceInfo = this.getDeviceInfo();
      console.log('[SessionChecker] Registering session with ID:', this.currentSessionId);
      
      const { data, error } = await supabase
        .from('user_sessions')
        .insert({
          id: this.currentSessionId,
          user_id: userId,
          device_info: deviceInfo,
          user_agent: navigator.userAgent,
          created_at: new Date().toISOString(),
          last_activity: new Date().toISOString(),
          is_active: true,
          client_type: 'web'
        })
        .select();

      if (error) {
        console.error('[SessionChecker] Error registering session:', error);
      } else {
        console.log('[SessionChecker] Current session registered successfully:', data);
      }
    } catch (error) {
      console.error('[SessionChecker] Error in registerCurrentSession:', error);
    }
  }

  private getDeviceInfo(): string {
    const userAgent = navigator.userAgent;
    const platform = navigator.platform;
    const language = navigator.language;
    
    return `${platform} - ${language} - ${userAgent.substring(0, 50)}`;
  }

  private startPeriodicCheck(userId: string): void {
    // Controlla ogni 15 secondi se la sessione è ancora valida (ridotto per essere più reattivo)
    this.checkInterval = setInterval(async () => {
      await this.checkSessionValidity(userId);
    }, 15000);

    // Aggiunge listener per quando la pagina torna in primo piano
    // Questo risolve il problema dei browser che fermano setInterval in background
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        console.log('[SessionChecker] Page became visible, checking session immediately');
        await this.checkSessionValidity(userId);
      }
    };

    const handleFocus = async () => {
      console.log('[SessionChecker] Window focused, checking session immediately');
      await this.checkSessionValidity(userId);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    
    // Salva i listener per poterli rimuovere dopo
    this.visibilityListener = handleVisibilityChange;
    this.focusListener = handleFocus;
  }

  private async checkSessionValidity(userId: string): Promise<void> {
    if (!this.currentSessionId) return;

    try {
      // Aggiorna last_activity della sessione corrente
      await this.updateLastActivity();

      // Controlla se questa sessione è ancora attiva
      const { data: currentSession, error } = await supabase
        .from('user_sessions')
        .select('is_active, disconnection_reason')
        .eq('id', this.currentSessionId)
        .single();

      console.log('[SessionChecker] Periodic check - Session ID:', this.currentSessionId, 'Active:', currentSession?.is_active, 'Reason:', currentSession?.disconnection_reason);

      if (error) {
        // Errore di rete/DB — NON disconnettere, potrebbe essere un transient dopo tab switch
        console.warn('[SessionChecker] Check failed (network/DB error), skipping:', error.message);
        return;
      }

      if (!currentSession || !currentSession.is_active) {
        // Questa sessione è stata effettivamente disconnessa da altrove
        console.log('[SessionChecker] Session invalidated, reason:', currentSession?.disconnection_reason || 'unknown');
        await this.handleSessionDisconnected(currentSession?.disconnection_reason || 'unknown');
      }

    } catch (error) {
      console.error('[SessionChecker] Error in checkSessionValidity:', error);
    }
  }

  private async updateLastActivity(): Promise<void> {
    if (!this.currentSessionId) return;

    try {
      const { error } = await supabase
        .from('user_sessions')
        .update({ 
          last_activity: new Date().toISOString() 
        })
        .eq('id', this.currentSessionId);

      if (error) {
        console.error('[SessionChecker] Error updating last activity:', error);
      }
    } catch (error) {
      console.error('[SessionChecker] Error in updateLastActivity:', error);
    }
  }

  private async handleSessionDisconnected(reason: string): Promise<void> {
    console.log('[SessionChecker] Handling session disconnection, reason:', reason);
    
    // Ferma il controllo periodico per evitare notifiche multiple
    this.stopPeriodicCheck();
    
    // Mostra messaggio all'utente e aspetta la sua conferma
    this.showDisconnectionMessage(reason);
  }

  private showDisconnectionMessage(reason: string): void {
    const messages = {
      'new_login': 'Un altro utente si è collegato con lo stesso account. Verrai disconnesso.',
      'timeout': 'La tua sessione è scaduta per inattività.',
      'admin_disconnect': 'La tua sessione è stata terminata da un amministratore.',
      'unknown': 'La tua sessione è stata terminata. Effettua nuovamente il login.'
    };

    const message = messages[reason as keyof typeof messages] || messages.unknown;
    
    // Crea un modal semplice
    const modal = document.createElement('div');
    modal.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]';
    modal.innerHTML = `
      <div class="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl">
        <div class="flex items-center mb-4">
          <div class="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center mr-3">
            <svg class="w-5 h-5 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path>
            </svg>
          </div>
          <h3 class="text-lg font-semibold text-gray-900">Sessione Terminata</h3>
        </div>
        <p class="text-gray-600 mb-6">${message}</p>
        <div class="flex justify-end">
          <button id="session-disconnect-ok" class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
            Disconnetti
          </button>
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    const okButton = modal.querySelector('#session-disconnect-ok');
    okButton?.addEventListener('click', async () => {
      if (document.body.contains(modal)) {
        document.body.removeChild(modal);
      }
      // Disconnetti l'utente quando clicca il pulsante
      await this.forceLogout();
    });

    // Impedisci la chiusura del modal cliccando fuori
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        e.preventDefault();
        e.stopPropagation();
      }
    });
  }

  private async forceLogout(): Promise<void> {
    try {
      await supabase.auth.signOut({ scope: 'local' });
    } catch (error) {
      console.error('[SessionChecker] Error during force logout:', error);
      // Forza il reload della pagina come fallback
      window.location.href = '/login';
    }
  }

  private stopPeriodicCheck(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
    
    // Rimuovi anche i listener di visibilità e focus
    if (this.visibilityListener) {
      document.removeEventListener('visibilitychange', this.visibilityListener);
      this.visibilityListener = null;
    }
    
    if (this.focusListener) {
      window.removeEventListener('focus', this.focusListener);
      this.focusListener = null;
    }
  }

  // Chiamato al logout per pulire tutto
  public cleanup(): void {
    console.log('[SessionChecker] Cleaning up...');
    this.stopPeriodicCheck();
    this.currentSessionId = null;
    this.isInitialized = false;
  }

  // Metodo pubblico per disconnettere altre sessioni (per admin)
  public async disconnectOtherSessions(userId: string): Promise<void> {
    await this.disconnectPreviousSessions(userId);
  }
}

// Esporta un'istanza singleton
export const sessionChecker = new SimpleSessionChecker();